<template>
  <div class="mb-4 flex justify-between">
    <p>{{ $n(sliderMinValue, 'currency') }}</p>
    <p>{{ $n(sliderMaxValue, 'currency') }}</p>
  </div>
  <div
    ref="slider"
    class="custom-slider minmax"
  >
    <div class="minmax-indicator" />
    <input
      id="min"
      ref="inputMin"
      type="range"
      name="min"
      :min="min"
      :max="max"
      :value="minValue"
      :step="step"
      @change="debounceInput"
    >
    <input
      id="max"
      ref="inputMax"
      type="range"
      name="max"
      :min="min"
      :max="max"
      :value="maxValue"
      :step="step"
      @change="debounceInput"
    >
  </div>
</template>

<script setup>
import { useDebounceFn } from '@vueuse/core';
// define component props for the slider component
const { min, max, step, minValue, maxValue, debounce } = defineProps({
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  minValue: {
    type: Number,
    default: 0,
  },
  maxValue: {
    type: Number,
    default: 100,
  },
  debounce: {
    type: Number,
    default: 0,
  },
});

// define emits for the slider component
const emit = defineEmits(['update:minValue', 'update:maxValue', 'on-apply-filter']);

// define refs for the slider element and the slider values
const slider = ref(null);
const inputMin = ref(null);
const inputMax = ref(null);
const sliderMinValue = ref(minValue);
const sliderMaxValue = ref(maxValue);

// function to get the percentage of a value between the min and max values
const getPercent = (value, min, max) => {
  return ((value - min) / (max - min)) * 100;
};

// function to set the css variables for width, left and right
const setCSSProps = (left, right) => {
  slider.value.style.setProperty('--progressLeft', `${left}%`);
  slider.value.style.setProperty('--progressRight', `${right}%`);
};

// watchEffect to emit the updated values, and update the css variables
// when the slider values change
watchEffect(() => {
  if (slider.value) {
    // emit slidet values when updated
    emit('update:minValue', sliderMinValue.value);
    emit('update:maxValue', sliderMaxValue.value);

    // calculate values in percentages
    const leftPercent = getPercent(sliderMinValue.value, min, max);
    const rightPercent = 100 - getPercent(sliderMaxValue.value, min, max);

    // set the CSS variables
    setCSSProps(leftPercent, rightPercent);
  }
});

const debounceInput = useDebounceFn((event) => {
  onInput(event);
}, debounce);

// validation sliderMinValue do not greater than sliderMaxValue and opposite
const onInput = ({ target }) => {
  if (target.name === 'min') {
    if (target.value > sliderMaxValue.value) {
      target.value = sliderMaxValue.value;
      sliderMinValue.value = sliderMaxValue.value;
    }
    else {
      sliderMinValue.value = parseFloat(target.value);
    }
    if (sliderMinValue.value < 0) {
      sliderMinValue.value = 0;
    }
  }

  if (target.name === 'max') {
    if (target.value < sliderMinValue.value) {
      target.value = sliderMinValue.value;
      sliderMaxValue.value = sliderMinValue.value;
    }
    else {
      sliderMaxValue.value = parseFloat(target.value);
    }
  }

  emit('on-apply-filter', sliderMinValue.value, sliderMaxValue.value);
};
</script>

<style lang="scss" scoped>
@media (hover: hover) {
  a:hover {
    background-color: hsla(160, 100%, 37%, 0.2);
  }
}

.custom-slider {
  --trackHeight: 0.35rem;
  --thumbRadius: 1rem;
  width: 100%;

  &.minmax {
    position: relative;
    height: var(--trackHeight);
    background: #fdc1ca;
    border-radius: 999px;
    margin: 0.5rem 0;
    --progressLeft: 0%;
    --progressRight: 0%;

    input[type="range"] {
      position: absolute;
      width: calc(100% - var(--thumbRadius));
    }

    input[type="range"][name="max"] {
      left: var(--thumbRadius);
    }

    input[type="range"]::-webkit-slider-runnable-track {
      background: none;
    }

    input[type="range"]::before {
      display: none;
    }
  }

  .minmax-indicator {
    position: absolute;
    height: 100%;
    pointer-events: none;
    left: var(--thumbRadius);
    right: var(--thumbRadius);

    &::before {
      content: "";
      position: absolute;
      background: #d00d30;
      height: 100%;
      left: var(--progressLeft);
      right: var(--progressRight);
    }
  }

  input[type="range"] {
    position: relative;
    appearance: none;
    background: none;
    border-radius: 999px;
    z-index: 0;
    height: 100%;
    pointer-events: none;

    &::before {
      content: "";
      display: block;
      position: absolute;
      width: var(--ProgressPercent, 100%);
      height: 100%;
      background: #d00d30;
      border-radius: 999px;
    }

    &::-webkit-slider-runnable-track {
      appearance: none;
      background: #4c0f17;
      height: var(--trackHeight);
      border-radius: 999px;
    }

    &::-webkit-slider-thumb {
      position: relative;
      width: var(--thumbRadius);
      height: var(--thumbRadius);
      margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
      background: #d00d30;
      border: 1px solid #d00d30;
      border-radius: 999px;
      pointer-events: all;
      appearance: none;
      z-index: 1;
    }

    &::-moz-range-track {
      appearance: none;
      background: #fdc1ca;
      height: var(--trackHeight);
      border-radius: 999px;
    }

    &::-moz-range-thumb {
      position: relative;
      box-sizing: border-box;
      width: var(--thumbRadius);
      height: var(--thumbRadius);
      margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
      background: #fdc1ca;
      border: 1px solid #fdc1ca;
      border-radius: 999px;
      pointer-events: all;
      appearance: none;
      z-index: 1;
    }
  }
}
</style>
